<template>
  <div class="page-container">
    <Navbar />

    <main class="page-content">
      <div class="page-header animate-fade-up">
        <div class="flex items-center justify-between">
          <div>
            <Breadcrumbs :items="[{ label: 'Supplier Users' }]" />
            <h1 class="page-title">Supplier User Management</h1>
            <p class="page-subtitle">Create and manage supplier user accounts</p>
          </div>
          <BaseButton
            variant="primary"
            @click="showModal = true"
            :icon="PlusIcon"
          >
            Add New User
          </BaseButton>
        </div>
      </div>

      <!-- Users Table -->
      <BaseCard class="animate-fade-up stagger-1">
        <template #header>
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-xl bg-accent-500/10 dark:bg-accent-500/15 flex items-center justify-center">
              <UsersIcon class="h-5 w-5 text-accent-600 dark:text-accent-400" />
            </div>
            <div>
              <h3 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
                Supplier Users
              </h3>
              <p class="text-xs text-surface-400 dark:text-surface-500 mt-0.5">
                <span class="font-mono">{{ users.length }}</span> total users
              </p>
            </div>
          </div>
        </template>

        <DataTable
          v-if="users.length"
          :headers="userHeaders"
          :items="users"
          :search-placeholder="`Search users`"
          :rows-per-page="25"
          :export-filename="`supplier-users.csv`"
        >
          <template #item-first_name="{ first_name }">
            <span class="font-medium text-surface-900 dark:text-surface-100">
              {{ first_name }}
            </span>
          </template>

          <template #item-last_name="{ last_name }">
            <span class="font-medium text-surface-900 dark:text-surface-100">
              {{ last_name }}
            </span>
          </template>

          <template #item-email="{ email }">
            <span class="font-mono text-sm text-surface-600 dark:text-surface-400">
              {{ email }}
            </span>
          </template>

          <template #item-contact_number="{ contact_number }">
            <span class="font-mono text-sm text-surface-600 dark:text-surface-400">
              {{ contact_number }}
            </span>
          </template>

          <template #item-role="{ role }">
            <span class="badge badge-primary">
              {{ role }}
            </span>
          </template>

          <template #empty>
            <EmptyState
              :icon="UsersIcon"
              title="No users found"
              message="There are no supplier users in the system. Create your first user to get started."
              variant="info"
            />
          </template>
        </DataTable>
      </BaseCard>
    </main>

    <!-- Add User Modal -->
    <transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="showModal" class="modal-overlay" @click="showModal = false"></div>
    </transition>

    <transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 transform scale-95"
      enter-to-class="opacity-100 transform scale-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 transform scale-100"
      leave-to-class="opacity-0 transform scale-95"
    >
      <div v-if="showModal" class="modal-container flex items-center justify-center p-4">
        <div class="modal-content max-w-2xl w-full" @click.stop>
          <!-- Modal Header -->
          <div class="px-6 py-5 border-b border-surface-200/60 dark:border-surface-800/60 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-xl bg-accent-500/10 dark:bg-accent-500/15 flex items-center justify-center">
                <PlusIcon class="h-5 w-5 text-accent-600 dark:text-accent-400" />
              </div>
              <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
                Add New Supplier User
              </h2>
            </div>
            <button
              @click="showModal = false"
              class="w-8 h-8 rounded-xl flex items-center justify-center text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800/60 transition-all duration-200"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>
          </div>

          <!-- Modal Body -->
          <form @submit.prevent="createUser" class="px-6 py-6 space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="label">First Name</label>
                <input
                  v-model="newUser.first_name"
                  type="text"
                  required
                  class="input"
                  placeholder="John"
                />
              </div>

              <div>
                <label class="label">Last Name</label>
                <input
                  v-model="newUser.last_name"
                  type="text"
                  required
                  class="input"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label class="label">Email</label>
              <input
                v-model="newUser.email"
                type="email"
                required
                class="input"
                placeholder="john.doe@example.com"
              />
            </div>

            <div>
              <label class="label">Contact Number</label>
              <input
                v-model="newUser.contact_number"
                type="tel"
                required
                class="input"
                placeholder="+971 50 123 4567"
              />
            </div>

            <div>
              <label class="label">Assign Groups</label>
              <select
                v-model="newUser.group"
                multiple
                required
                class="input min-h-[120px]"
              >
                <option
                  v-for="group in adminProfile.group"
                  :key="group.group_id"
                  :value="group"
                  class="py-2"
                >
                  {{ group.group_name }} - {{ group.site_name }}
                </option>
              </select>
              <p class="mt-2 text-sm text-surface-400 dark:text-surface-500">
                Hold <kbd class="px-1.5 py-0.5 text-xs font-semibold font-mono bg-surface-100 dark:bg-surface-800 border border-surface-200/80 dark:border-surface-700/60 rounded-lg">Ctrl</kbd> or <kbd class="px-1.5 py-0.5 text-xs font-semibold font-mono bg-surface-100 dark:bg-surface-800 border border-surface-200/80 dark:border-surface-700/60 rounded-lg">Cmd</kbd> to select multiple groups
              </p>
            </div>

            <div>
              <label class="label">User Role</label>
              <select v-model="newUser.role" required class="input">
                <option value="" disabled>Select a user role</option>
                <option value="VIEWER">Viewer</option>
              </select>
            </div>

            <!-- Error Message -->
            <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 -translate-y-2">
              <div v-if="error" class="rounded-2xl border border-red-200/80 dark:border-red-800/60 bg-red-50/80 dark:bg-red-900/20 backdrop-blur-sm p-4 flex items-start space-x-3">
                <ExclamationCircleIcon class="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p class="text-sm text-red-800 dark:text-red-200">{{ error }}</p>
              </div>
            </transition>

            <!-- Modal Footer -->
            <div class="flex justify-end space-x-3 pt-6 border-t border-surface-200/60 dark:border-surface-800/60">
              <BaseButton
                type="button"
                variant="secondary"
                @click="showModal = false"
              >
                Cancel
              </BaseButton>
              <BaseButton
                type="submit"
                variant="primary"
                :loading="isLoading"
                :icon="isLoading ? undefined : CheckIcon"
              >
                {{ isLoading ? 'Creating...' : 'Create User' }}
              </BaseButton>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <Footer />
  </div>
</template>

<script setup>
import { ref, watchEffect, computed } from 'vue'
import { db } from '@/firebase/config'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { useStore } from 'vuex'
import { useToast } from '@/composables/useToast'
import { authFetch } from '@/composables/useApi'

import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import Breadcrumbs from '@/components/ui/Breadcrumbs.vue'
import DataTable from '@/components/ui/DataTable.vue'
import EmptyState from '@/components/ui/EmptyState.vue'

import {
  PlusIcon,
  UsersIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationCircleIcon
} from '@heroicons/vue/24/outline'

const store = useStore()
const { success, error: showErrorToast } = useToast()

const users = ref([])
const usersRef = collection(db, 'Users')
const adminProfile = computed(() => store.state.userProfile)
const isLoading = ref(false)
const error = ref(null)
const showModal = ref(false)

const newUser = ref({
  first_name: '',
  last_name: '',
  email: '',
  contact_number: '',
  role: '',
  group: [],
})

const userHeaders = [
  { value: 'first_name', text: 'First Name', sortable: true },
  { value: 'last_name', text: 'Last Name', sortable: true },
  { value: 'email', text: 'Email', sortable: true },
  { value: 'contact_number', text: 'Contact Number' },
  { value: 'role', text: 'User Role', sortable: true },
]

const fetchUsers = async () => {
  let fetchedUsers = {}

  for (let group of adminProfile.value.group) {
    const q = query(usersRef, where('group', 'array-contains', group))
    const snapshot = await getDocs(q)

    snapshot.forEach((doc) => {
      fetchedUsers[doc.id] = doc.data()
    })
  }

  users.value = Object.values(fetchedUsers)
}

const createUser = async () => {
  isLoading.value = true
  error.value = null

  const apiUrl = store.state.apiUrl + 'create_viewer_account/'
  const userData = {
    first_name: newUser.value.first_name,
    last_name: newUser.value.last_name,
    email: newUser.value.email,
    customer_logo: adminProfile.value.customer_logo,
    primary_account: adminProfile.value.primary_account,
    contact_number: newUser.value.contact_number,
    role: newUser.value.role,
    group: newUser.value.group,
    account_type: 'SUPPLIER',
  }

  try {
    const response = await authFetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    }, store.state.user)

    if (!response.ok) {
      throw new Error('Failed to create user')
    }

    if (response.status === 400 || response.status === 405) {
      throw new Error('There was an error completing the request.')
    }

    await fetchUsers()

    newUser.value = {
      first_name: '',
      last_name: '',
      email: '',
      contact_number: '',
      role: '',
      group: [],
    }

    showModal.value = false
    success('User created successfully! Login credentials have been sent via email.')
  } catch (err) {
    error.value = err.message
    showErrorToast(err.message)
  } finally {
    isLoading.value = false
  }
}

watchEffect(() => {
  if (adminProfile.value) {
    fetchUsers()
  }
})
</script>
